<template>
  <div style=" position: relative; width: 100%; height: 100%;min-height: 200px">
    <div
      ref="echartsRef"
      style=" width: 100%;min-width: 370px; height: 100%; min-height: 200px"
    />
  </div>
</template>
<script setup>
import * as echarts from "echarts";

let myChart = null;
const emits = defineEmits(["onClick"]);
const echartsRef = ref(null);
const Props = defineProps({
  options: Object,
  optionsData: Object || Array
});
const state = reactive({
  newOptionsData: Props.options,
  options: {
    color: [
      "#00875A",
      "#0084FF",
      "#536587",
      "#FFA64C",
      "#877AFC",
      "#4DD3F1",
      "#F74D57",
      "#FDC1D9",
      "#7D655A"
    ],
    tooltip: {},
    legend: {
      left: "center",
      top: "bottom",
      show: true,
      icon: "path://M512 95.8976A416.1024 416.1024 0 1 0 928.1024 512 416.0512 416.0512 0 0 0 512 95.8976z m0 608.256A192.1536 192.1536 0 1 1 704.1536 512 192.1536 192.1536 0 0 1 512 704.1536z"
    },
    dataset: {
      dimensions: [],
      source: []
    },
    xAxis: { type: "category", boundaryGap: false },
    yAxis: {},
    series: [],
    grid: {
      bottom: 60,
      left: "left",
      top: 20,
      right: 60,
      containLabel: true
    }
  },
  gaugeData: []
});

const options = computed(() => {
  return Props.optionsData;
});

nextTick(() => {
  initChart();
});
onMounted(() => {
  watch(
    () => Props.options,
    val => {
      initData();
    },
    { deep: true }
  );
  watch(
    () => options.value,
    value => {
      state.newOptionsData.options = value;
      initData();
    },
    { deep: true }
  );
});

window.addEventListener("resize", () => {
  if (myChart?.id) {
    myChart.resize();
  }
});

// 初始化数据
async function initData() {
  state.options.color = Props.options.colors;
  state.options.legend = {
    ...state.options.legend,
    ...Props.options.legend
  };
  state.options.dataset.dimensions = [];
  state.options.series = [];
  if (options.value?.length) {
    Object.keys(options.value[0]).forEach(item => {
      if (item !== "businessId") {
        state.options.dataset.dimensions.push(item);
      }
    });
    state.options.dataset.source = options.value;
  }
  await setChartType(
    Props.options.type,
    state.options.dataset.dimensions,
    state.options.dataset.source
  );
  setChart();
}

function setChartType(type = "bar", series, source) {
  if (type === "pie") {
    delete state.options.xAxis;
    delete state.options.yAxis;

    if (Props.options?.sumTotal.show) {
      const sumTotal = () => {
        let total = 0; //计算总和保留整数
        source.forEach(item => {
          total += item[series[1]];
        });
        //将数字格式化为带有千位分隔符的字符串
        return Math.floor(total).toLocaleString();
      };
      state.options.title = {
        show: true,
        text: Props.options?.sumTotal.title,
        left: "center",
        top: "center",
        textStyle: {
          color: "#333",
          fontSize: Props.options?.sumTotal.textStyle.fontSize,
          align: "center"
        },
        subtext: sumTotal(),
        subtextStyle: {
          color: "#333",
          fontSize: Props.options?.sumTotal.subtextStyle.fontSize,
          align: "center",
          fontWeight: 600
        }
      };
    } else {
      state.options.title = {
        show: false
      };
    }
    series.forEach((item, index) => {
      if (index) {
        state.options.series.push({
          type: "pie",
          name: item,
          radius: [
            Props.options.isRadius ? `${Props.options.radiusInner}%` : "0%",
            `${Props.options.radius}%`
          ],
          center: setCenter(series.length, index),
          label: {
            position: Props.options.label.position || "outside",
            fontSize: Props.options.label.fontSize || 12,
            color: Props.options.label.color || "#333",
            show: Props.options.label.show,
            formatter: "{d}%",
            textBorderColor: "inherit"
          },
          itemStyle: {
            borderColor: "#fff",
            borderRadius: Props.options.borderRadius,
            borderWidth: Props.options.borderWidth
          },
          encode: {
            itemName: series[0],
            value: item
          }
        });
      }
    });
  }
  if (type === "bar" || type === "line") {
    state.options.xAxis.boundaryGap = type === "bar";

    const show = source.length > 24;
    series.forEach((item, index) => {
      if (index) {
        const seriesItem = {
          type: type,
          smooth: Props.options.smooth,
          barMaxWidth: Props.options.barMaxWidth,
          lineStyle: {
            width: Props.options.barMaxWidth
          },
          symbol: Props.options.symbol,
          symbolSize: 6
        };
        if (type === "line" && Props.options?.areaStyle.show) {
          seriesItem.areaStyle = {
            opacity: 0.1
          };
        }
        if (Props.options.stack) {
          seriesItem.stack = "Total";
        }
        state.options.series.push(seriesItem);
      }
    });
    state.options.dataZoom = [
      {
        show: show,
        type: "slider",
        height: 0,
        bottom: 30,
        start: 0,
        end: source.length > 24 ? 50 : 100,
        borderColor: "transparent"
        // fillerColor: "#00875A",
        // moveHandleStyle  :{
        //   color: "#00875A",
        //   opacity: 1
        // }
      },
      {
        type: "inside"
      }
    ];
  }

  if (type === "strip") {
    state.options.xAxis.type = "value";
    state.options.yAxis.type = "category";
    series.forEach((item, index) => {
      if (index) {
        state.options.series.push({
          type: "bar",
          smooth: true,
          barMaxWidth: Props.options.barMaxWidth
        });
      }
    });
  }
  if (type === "scatter") {
    state.options.xAxis = {
      name: Props.options.xAxis.name || "",
      axisLabel: {
        formatter: function (value, index) {
          return value + Props.options.xAxis.nameUnit || "";
        }
      },
      splitLine: { show: false }
    };
    state.options.yAxis = {
      name: Props.options.yAxis.name || "",
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        formatter: function (value, index) {
          return value + Props.options.yAxis.nameUnit || "";
        }
      }
    };
    state.options.legend.data = source.map(item => {
      return item.name;
    });
    state.options.series = source.map(item => {
      return {
        name: item.name,
        type: "scatter",
        data: [[item.x, item.y, item.z]],
        symbolSize: function () {
          return item.z * 5;
        }
      };
    });
  }
  if (type === "gauge") {
    delete state.options.xAxis;
    delete state.options.yAxis;
    state.options.series = [];
    const item = {
      name: "",
      type: "gauge",
      progress: {
        show: true,
        width: 8
      },
      axisLine: {
        lineStyle: {
          width: 4
        }
      },
      axisTick: {
        distance: 0,
        length: 10,
        lineStyle: {
          color: "#999",
          width: 0.5
        }
      },
      splitLine: {
        length: 9,
        distance: 4,
        lineStyle: {
          width: 1,
          color: "#999"
        }
      },

      axisLabel: {
        distance: 10,
        color: "#999",
        fontSize: 10,
        formatter: function (value) {
          return Math.floor(value * 100) / 100;
        }
      },
      title: {
        show: true,
        offsetCenter: [0, "25%"],
        fontSize: 12,
        fontWeight: "bold"
      },
      detail: {
        valueAnimation: true,
        fontSize: 30,
        fontWeight: "bold",
        offsetCenter: [0, "50%"],
        formatter: "{value}"
      },
      max: state.gaugeData[0]?.total || 0,
      data: [
        {
          value: state.gaugeData[0]?.value || 0,
          name: state.gaugeData[0]?.name || ""
        }
      ]
    };
    state.options.series.push(item);
  }

  function setCenter(length, i) {
    let x = "0";
    let y = "0";
    x = `${(100 / (length * 2)) * i + (100 / (length * 2)) * i}%`;
    y = `${100 / 2}%`;
    return [x, y];
  }
}

// 基于准备好的dom，初始化echarts实例
function initChart() {
  myChart = echarts.init(echartsRef.value);

  initData();
}

// 绘制图表
function setChart() {
  if (myChart) myChart.dispose(); // myChart.dispose() // myChart.clear()
  myChart = echarts.init(echartsRef.value);
  myChart.setOption(state.options);
  myChart.resize();
  myChart.on("click", function (params) {
    console.log("图表点击", params);
    emits("onClick");
  });
}
</script>
